<template>
  <v-container id="user-profile" fluid tag="section" class="profile-page">
    <v-dialog
      v-model="submitted"
      :persistent="redirectAfterSuccess"
      max-width="460"
    >
      <v-card class="password-success-dialog border-radius-xl">
        <v-card-text class="pa-7 pa-sm-9 text-center">
          <div class="password-success-dialog__icon mx-auto mb-5">
            <v-icon color="white" size="34">mdi-check</v-icon>
          </div>
          <h3 class="password-success-dialog__title">
            {{ successDialogTitle }}
          </h3>
          <p v-if="redirectAfterSuccess" class="text-body mt-3 mb-7">
            {{ $t("Senha_atualizada_sucesso_detalhe") }}
          </p>
          <v-btn
            color="#516a78"
            class="font-weight-bold white--text px-7"
            :block="redirectAfterSuccess"
            depressed
            large
            @click="closeSuccessDialog"
          >
            {{
              redirectAfterSuccess ? $t("Continuar_para_o_sistema") : $t("Ok")
            }}
            <v-icon v-if="redirectAfterSuccess" right> mdi-arrow-right </v-icon>
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-row justify="center">
      <v-col cols="12" lg="10" xl="9">
        <div v-if="errors.length > 0" class="mb-4">
          <div v-for="(error, index) in errors" :key="`error-${index}`">
            <base-material-alert color="error" dark dismissible>
              <span>{{ formatErrorLabel(error) }}</span> -
              {{ formatErrorMessage(error) }}
            </base-material-alert>
          </div>
        </div>

        <v-card
          v-if="isExpiredPasswordFlow"
          class="password-renewal card-shadow border-radius-xl"
        >
          <section class="password-renewal__intro">
            <div class="password-renewal__icon mb-6">
              <v-icon color="white" size="34">mdi-lock-reset</v-icon>
            </div>
            <div class="password-renewal__eyebrow mb-2">
              {{ $t("Seguranca_da_conta") }}
            </div>
            <h1 class="password-renewal__title">
              {{ $t("Alteracao_senha_obrigatoria") }}
            </h1>
            <p class="password-renewal__description mt-4 mb-0">
              {{ passwordChangeMessage }}
            </p>

            <div class="password-renewal__account mt-7">
              <v-avatar size="42" class="password-renewal__avatar">
                {{ profileInitials }}
              </v-avatar>
              <div class="password-renewal__account-data">
                <strong>{{ profileName }}</strong>
                <span>{{ profileEmail || "-" }}</span>
              </div>
            </div>

            <div class="password-renewal__security-note mt-7">
              <v-icon color="rgba(255,255,255,.82)" size="18">
                mdi-shield-check-outline
              </v-icon>
              <span>{{ $t("Sessao_protegida_ao_atualizar_senha") }}</span>
            </div>
          </section>

          <section class="password-renewal__form-panel">
            <div class="password-renewal__step mb-4">
              <v-icon size="16">mdi-check-circle-outline</v-icon>
              {{ $t("Ultima_etapa") }}
            </div>
            <h2 class="password-renewal__form-title">
              {{ $t("Crie_uma_nova_senha") }}
            </h2>
            <p class="text-body mt-2 mb-6">
              {{ $t("Crie_uma_nova_senha_descricao") }}
            </p>

            <v-form @submit.prevent="submitForm">
              <v-text-field
                ref="expiredPasswordInput"
                v-model="form.senha"
                :label="$t('Nova_senha')"
                name="senha"
                :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show1 ? 'text' : 'password'"
                :error-messages="senhaErrors"
                :success="passwordHasMinimumLength"
                @click:append="show1 = !show1"
                @input="onPasswordInput"
                @blur="touched.senha = true"
                hide-details="auto"
                outlined
                color="#516a78"
                light
                autocomplete="new-password"
                autofocus
                :disabled="isSubmitting"
                class="input-style font-size-input text-light-input input-icon"
              />

              <v-text-field
                v-model="form.senhaConfirma"
                :label="$t('Confirmar_nova_senha')"
                name="senhaConfirma"
                :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                :type="show2 ? 'text' : 'password'"
                :error-messages="senhaConfirmaErrors"
                :success="passwordsMatch"
                @click:append="show2 = !show2"
                @input="onPasswordConfirmInput"
                @blur="touched.senhaConfirma = true"
                hide-details="auto"
                outlined
                color="#516a78"
                light
                autocomplete="new-password"
                :disabled="isSubmitting"
                class="
                  input-style
                  font-size-input
                  text-light-input
                  input-icon
                  mt-4
                "
              />

              <div class="password-requirements mt-5 mb-6">
                <div class="password-requirements__title">
                  {{ $t("Sua_senha_precisa") }}
                </div>
                <div
                  class="password-requirement"
                  :class="{
                    'password-requirement--valid': passwordHasMinimumLength,
                  }"
                >
                  <v-icon size="18">
                    {{
                      passwordHasMinimumLength
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  <span>{{ minimumLengthRequirement }}</span>
                </div>
                <div
                  class="password-requirement"
                  :class="{ 'password-requirement--valid': passwordsMatch }"
                >
                  <v-icon size="18">
                    {{
                      passwordsMatch
                        ? "mdi-check-circle"
                        : "mdi-checkbox-blank-circle-outline"
                    }}
                  </v-icon>
                  <span>{{ $t("Senhas_devem_ser_iguais") }}</span>
                </div>
              </div>

              <v-btn
                color="#516a78"
                class="font-weight-bold white--text"
                type="submit"
                :loading="isSubmitting"
                :disabled="isSubmitDisabled"
                block
                depressed
                large
              >
                {{ $t("Salvar_nova_senha_continuar") }}
                <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-form>
          </section>
        </v-card>

        <v-row v-else>
          <v-col cols="12" md="4">
            <v-card
              class="
                profile-section profile-summary
                card-shadow
                border-radius-xl
              "
              id="profile-summary"
            >
              <div
                class="profile-section__header profile-section__header--center"
              >
                <div>
                  <div class="profile-section__eyebrow">
                    {{ $t("Identificacao") }}
                  </div>
                  <h5 class="profile-section__title">
                    {{ $t("Dados_do_perfil") }}
                  </h5>
                </div>
              </div>

              <div class="profile-summary__avatar">
                <ShowImage v-if="profileImage" :url="profileImage">
                  <button
                    type="button"
                    class="profile-avatar profile-avatar--image"
                  >
                    <img
                      class="profile-avatar__img"
                      :src="profileImage"
                      :alt="$t('Foto_do_usuario')"
                    />
                  </button>
                </ShowImage>

                <v-avatar
                  v-else
                  size="132"
                  class="profile-avatar profile-avatar--placeholder"
                >
                  {{ profileInitials }}
                </v-avatar>
              </div>

              <div class="profile-summary__name">
                {{ profileName }}
              </div>
              <div class="profile-summary__email">
                {{ profileEmail || "-" }}
              </div>

              <v-divider class="my-5" />

              <div class="profile-summary__info">
                <!-- <div class="profile-info-row">
                  <v-icon size="18">mdi-account-outline</v-icon>
                  <span>{{ $t("Pessoa") }}</span>
                  <strong>{{ pessoa.id || "-" }}</strong>
                </div> -->

                <div class="profile-info-row">
                  <v-icon size="18">mdi-phone-outline</v-icon>
                  <span>{{ $t("Telefone") }}</span>
                  <strong>{{ profilePhone || "-" }}</strong>
                </div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="8">
            <v-card
              class="profile-section card-shadow border-radius-xl"
              id="contact"
            >
              <div class="profile-section__header">
                <div class="profile-section__heading">
                  <v-icon class="profile-section__icon">
                    mdi-card-account-phone-outline
                  </v-icon>
                  <div>
                    <div class="profile-section__eyebrow">
                      {{ $t("Acessos") }}
                    </div>
                    <h5 class="profile-section__title">
                      {{ $t("Contato_de_acesso") }}
                    </h5>
                  </div>
                </div>

                <v-tooltip v-if="canEditOwnContact && !isEditingContact" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="isContactSubmitting"
                      @click="startContactEdition"
                    >
                      <v-icon>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("Alterar_email_telefone") }}</span>
                </v-tooltip>
              </div>

              <div v-if="!isEditingContact" class="contact-list">
                <div class="contact-item">
                  <v-icon>mdi-email-outline</v-icon>
                  <div class="contact-item__content">
                    <span>{{ $t("Email") }}</span>
                    <strong>{{ profileEmail || "-" }}</strong>
                  </div>
                </div>

                <div class="contact-item">
                  <v-icon>mdi-phone-outline</v-icon>
                  <div class="contact-item__content">
                    <span>{{ $t("Telefone") }}</span>
                    <strong>{{ profilePhone || "-" }}</strong>
                  </div>
                </div>

                <v-alert
                  v-if="!canEditOwnContact"
                  dense
                  outlined
                  type="info"
                  class="mt-4 mb-0"
                >
                  {{ $t("Edicao_contato_indisponivel") }}
                </v-alert>
              </div>

              <v-form v-else @submit.prevent="submitContactForm">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contactForm.email"
                      :label="$t('Email')"
                      name="email"
                      type="email"
                      inputmode="email"
                      autocomplete="email"
                      :hint="$t('Email_alteracao_dica')"
                      :error-messages="contactEmailErrors"
                      persistent-hint
                      hide-details="auto"
                      outlined
                      light
                      color="rgba(0,0,0,.6)"
                      :disabled="isContactSubmitting"
                      class="
                        input-style
                        font-size-input
                        text-light-input
                        placeholder-light
                      "
                      @input="onContactFieldInput('email')"
                      @blur="contactTouched.email = true"
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="contactForm.telefone"
                      :label="$t('Telefone')"
                      name="telefone"
                      type="tel"
                      inputmode="tel"
                      autocomplete="tel"
                      maxlength="13"
                      :hint="$t('Telefone_alteracao_dica')"
                      :error-messages="contactPhoneErrors"
                      persistent-hint
                      hide-details="auto"
                      outlined
                      light
                      color="rgba(0,0,0,.6)"
                      :disabled="isContactSubmitting"
                      class="
                        input-style
                        font-size-input
                        text-light-input
                        placeholder-light
                      "
                      @input="onPhoneInput"
                      @blur="contactTouched.telefone = true"
                    />
                  </v-col>

                  <v-col cols="12" class="profile-actions">
                    <v-btn
                      text
                      color="#516a78ee"
                      :disabled="isContactSubmitting"
                      @click="cancelContactEdition"
                    >
                      {{ $t("Cancelar") }}
                    </v-btn>

                    <v-btn
                      color="#516a78ee"
                      class="font-weight-bolder btn-default bg-gradient-default"
                      type="submit"
                      :loading="isContactSubmitting"
                      :disabled="isContactSubmitDisabled"
                    >
                      {{ $t("Salvar") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>

            <v-card
              class="profile-section card-shadow border-radius-xl mt-6"
              id="change"
            >
              <div class="profile-section__header">
                <div class="profile-section__heading">
                  <v-icon class="profile-section__icon">
                    mdi-lock-reset
                  </v-icon>
                  <div>
                    <div class="profile-section__eyebrow">
                      {{ $t("Seguranca") }}
                    </div>
                    <h5 class="profile-section__title">
                      {{ $t("Atualizar_senha") }}
                    </h5>
                  </div>
                </div>
              </div>

              <v-form @submit.prevent="submitForm">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.senha"
                      :label="$t('Nova_senha')"
                      name="senha"
                      :append-icon="show1 ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="show1 ? 'text' : 'password'"
                      :error-messages="senhaErrors"
                      :hint="senhaHint"
                      persistent-hint
                      @click:append="show1 = !show1"
                      @input="onPasswordInput"
                      @blur="touched.senha = true"
                      hide-details="auto"
                      outlined
                      color="rgba(0,0,0,.6)"
                      light
                      :placeholder="$t('Nova_senha')"
                      autocomplete="new-password"
                      :disabled="isSubmitting"
                      class="
                        input-style
                        font-size-input
                        text-light-input
                        placeholder-light
                        input-icon
                      "
                    />
                  </v-col>

                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="form.senhaConfirma"
                      :label="$t('Confirmar_nova_senha')"
                      name="senhaConfirma"
                      :append-icon="show2 ? 'mdi-eye' : 'mdi-eye-off'"
                      :type="show2 ? 'text' : 'password'"
                      :error-messages="senhaConfirmaErrors"
                      @click:append="show2 = !show2"
                      @input="onPasswordConfirmInput"
                      @blur="touched.senhaConfirma = true"
                      hide-details="auto"
                      outlined
                      color="rgba(0,0,0,.6)"
                      light
                      :placeholder="$t('Confirmar_nova_senha')"
                      autocomplete="new-password"
                      :disabled="isSubmitting"
                      class="
                        input-style
                        font-size-input
                        text-light-input
                        placeholder-light
                        input-icon
                      "
                    />
                  </v-col>

                  <v-col cols="12" class="profile-actions">
                    <v-btn
                      color="#516a78ee"
                      class="
                        font-weight-bolder
                        btn-default
                        bg-gradient-default
                        py-4
                        px-8
                      "
                      type="submit"
                      :loading="isSubmitting"
                      :disabled="isSubmitDisabled"
                    >
                      {{
                        passwordChangeRequired
                          ? $t("Atualizar_senha")
                          : $t("Atualizar")
                      }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card
              class="profile-section card-shadow border-radius-xl"
              id="appearance"
            >
              <div class="profile-section__header">
                <div class="profile-section__heading">
                  <v-icon class="profile-section__icon">
                    mdi-format-size
                  </v-icon>
                  <div>
                    <div class="profile-section__eyebrow">
                      {{ $t("Aparencia") }}
                    </div>
                    <h5 class="profile-section__title">
                      {{ $t("Tamanho_do_texto") }}
                    </h5>
                  </div>
                </div>
              </div>

              <p class="font-size-preference__description">
                {{ $t("Tamanho_do_texto_descricao") }}
              </p>

              <div
                class="font-size-options"
                role="radiogroup"
                :aria-label="$t('Tamanho_do_texto')"
              >
                <button
                  v-for="option in fontSizeOptions"
                  :key="option.value"
                  type="button"
                  class="font-size-option"
                  :class="{
                    'font-size-option--selected':
                      fontSizePreference === option.value,
                  }"
                  role="radio"
                  :aria-checked="fontSizePreference === option.value"
                  :disabled="isFontSizeSubmitting"
                  @click="selectFontSizePreference(option.value)"
                >
                  <span
                    class="font-size-option__sample"
                    :style="{ fontSize: option.sampleSize }"
                    aria-hidden="true"
                  >
                    Aa
                  </span>
                  <span class="font-size-option__content">
                    <span class="font-size-option__heading">
                      <strong>{{ option.label }}</strong>
                      <span class="font-size-option__badge">
                        {{ option.badge }}
                      </span>
                    </span>
                    <span class="font-size-option__description">
                      {{ option.description }}
                    </span>
                  </span>
                  <v-icon
                    class="font-size-option__check"
                    :color="
                      fontSizePreference === option.value
                        ? '#516a78'
                        : '#b4bec4'
                    "
                  >
                    {{
                      fontSizePreference === option.value
                        ? "mdi-check-circle"
                        : "mdi-radiobox-blank"
                    }}
                  </v-icon>
                </button>
              </div>

              <div class="font-size-preference__footer">
                <span class="font-size-preference__hint">
                  <v-icon size="17">mdi-eye-outline</v-icon>
                  {{ $t("Alteracao_tamanho_texto_previa") }}
                </span>
                <v-btn
                  color="#516a78ee"
                  class="font-weight-bolder btn-default bg-gradient-default"
                  :loading="isFontSizeSubmitting"
                  :disabled="
                    isFontSizeSubmitting || !hasFontSizePreferenceChanges
                  "
                  @click="saveFontSizePreference"
                >
                  <v-icon left>mdi-content-save-outline</v-icon>
                  {{ $t("Salvar_preferencia") }}
                </v-btn>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card
              class="profile-section card-shadow border-radius-xl"
              id="notifications-preference"
            >
              <div class="profile-section__header">
                <div class="profile-section__heading">
                  <v-icon class="profile-section__icon">
                    mdi-bell-outline
                  </v-icon>
                  <div>
                    <div class="profile-section__eyebrow">
                      {{ $t("Notificacoes") }}
                    </div>
                    <h5 class="profile-section__title">
                      {{ $t("Notificacoes_no_portal") }}
                    </h5>
                  </div>
                </div>
              </div>

              <div class="notification-preference">
                <div class="notification-preference__copy">
                  <strong>{{ $t("Exibir_alertas_push_no_portal") }}</strong>
                  <span>
                    {{ $t("Exibir_alertas_push_no_portal_descricao") }}
                  </span>
                </div>
                <v-switch
                  v-model="notificationsPreference"
                  color="#516a78"
                  inset
                  hide-details
                  :disabled="isNotificationsPreferenceSubmitting"
                  :aria-label="$t('Exibir_alertas_push_no_portal')"
                />
              </div>

              <div class="font-size-preference__footer">
                <span class="font-size-preference__hint">
                  <v-icon size="17">mdi-information-outline</v-icon>
                  {{ $t("Notificacoes_no_portal_observacao") }}
                </span>
                <v-btn
                  color="#516a78ee"
                  class="font-weight-bolder btn-default bg-gradient-default"
                  :loading="isNotificationsPreferenceSubmitting"
                  :disabled="
                    isNotificationsPreferenceSubmitting ||
                    !hasNotificationsPreferenceChanges
                  "
                  @click="saveNotificationsPreference"
                >
                  <v-icon left>mdi-content-save-outline</v-icon>
                  {{ $t("Salvar_preferencia") }}
                </v-btn>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card
              class="profile-section card-shadow border-radius-xl"
              id="trusted-devices"
            >
              <div class="profile-section__header">
                <div class="profile-section__heading">
                  <v-icon class="profile-section__icon">
                    mdi-cellphone-key
                  </v-icon>
                  <div>
                    <div class="profile-section__eyebrow">
                      {{ $t("Seguranca") }}
                    </div>
                    <h5 class="profile-section__title">
                      {{ $t("Dispositivos_confiaveis") }}
                    </h5>
                  </div>
                </div>
              </div>

              <v-alert
                v-if="dispositivosConfiaveisErro"
                type="error"
                dense
                outlined
              >
                {{ dispositivosConfiaveisErro }}
              </v-alert>

              <v-progress-linear
                v-if="carregandoDispositivosConfiaveis"
                indeterminate
                color="#516a78ee"
                class="mb-4"
              />

              <v-alert
                v-if="
                  !carregandoDispositivosConfiaveis &&
                  dispositivosConfiaveis.length === 0
                "
                type="info"
                dense
                outlined
              >
                {{ $t("Nenhum_dispositivo_confiavel") }}
              </v-alert>

              <v-list
                v-if="
                  !carregandoDispositivosConfiaveis &&
                  dispositivosConfiaveis.length > 0
                "
                two-line
                class="trusted-devices-list"
              >
                <v-list-item
                  v-for="dispositivo in dispositivosConfiaveis"
                  :key="trustedDeviceKey(dispositivo)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ dispositivo.descricao }}
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ formatTrustedDeviceDates(dispositivo) }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-btn
                      icon
                      color="error"
                      :loading="
                        removendoDispositivoConfiavelId ===
                        trustedDeviceKey(dispositivo)
                      "
                      @click="removerDispositivoConfiavel(dispositivo)"
                    >
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ShowImage from "@/components/base/proprios/ShowImage";
import UsuarioDataService from "@/services/acessos/UsuarioDataService";
import PessoaDataService from "@/services/pessoal/PessoaDataService";
import {
  getPasswordPolicy,
  passwordMeetsPolicy,
  passwordPolicyLabel,
} from "@/utils/passwordPolicy";
import {
  applyFontSizePreference,
  FONT_SIZE_PREFERENCES,
  normalizeFontSizePreference,
} from "@/utils/fontSizePreference";

const CONTACT_PARAMETRIZATION = "APPPERMITIRAJUSTEENDERECOEMAIL";
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function normalizeEmailValue(value) {
  return `${value || ""}`.trim().toLowerCase();
}

function normalizePhoneValue(value) {
  return `${value || ""}`.replace(/\D/g, "").slice(0, 13);
}

function getPrimaryPhoneValue(telefones) {
  if (!Array.isArray(telefones)) {
    return "";
  }
  const telefone = telefones.find((item) => item && item.fone);
  return telefone ? telefone.fone : "";
}

export default {
  name: "Perfil",
  components: {
    ShowImage,
  },
  data() {
    const user = this.$store.state.user || {};
    const pessoa = user.pessoa || {};
    const tamanhoFonte = normalizeFontSizePreference(user.tamanhoFonte);

    return {
      show1: false,
      show2: false,
      submitted: false,
      successMessageKey: "",
      isSubmitting: false,
      isContactSubmitting: false,
      isEditingContact: false,
      isFontSizeSubmitting: false,
      isNotificationsPreferenceSubmitting: false,
      fontSizePreference: tamanhoFonte,
      savedFontSizePreference: tamanhoFonte,
      notificationsPreference: user.permitirNotificacoes !== false,
      savedNotificationsPreference: user.permitirNotificacoes !== false,
      redirectAfterSuccess: false,
      errors: [],
      dispositivosConfiaveis: [],
      carregandoDispositivosConfiaveis: false,
      removendoDispositivoConfiavelId: null,
      dispositivosConfiaveisErro: "",
      contactForm: {
        email: normalizeEmailValue(pessoa.email),
        telefone: normalizePhoneValue(getPrimaryPhoneValue(pessoa.telefones)),
      },
      contactTouched: {
        email: false,
        telefone: false,
      },
      form: {
        senha: "",
        senhaConfirma: "",
      },
      touched: {
        senha: false,
        senhaConfirma: false,
      },
    };
  },
  computed: {
    passwordPolicyType() {
      return (
        this.$store.state?.inquilino?.tipoComplexidadeSenha ||
        this.$store.state?.user?.inquilino?.tipoComplexidadeSenha
      );
    },
    minPasswordLength() {
      return getPasswordPolicy(this.passwordPolicyType).minLength;
    },
    user() {
      return this.$store.state.user || {};
    },
    userId() {
      return this.user.id || null;
    },
    pessoa() {
      return this.user.pessoa || {};
    },
    profileName() {
      return this.pessoa.nome || this.user.nome || this.$t("usuario");
    },
    profileEmail() {
      return this.pessoa.email || this.user.email || "";
    },
    profileEmailNormalized() {
      return normalizeEmailValue(this.profileEmail);
    },
    profilePhoneRaw() {
      return normalizePhoneValue(getPrimaryPhoneValue(this.pessoa.telefones));
    },
    profilePhone() {
      return this.formatPhone(this.profilePhoneRaw);
    },
    profileImage() {
      return this.pessoa.imagem || "";
    },
    profileInitials() {
      const text = this.profileName || this.profileEmail || "U";
      const initials = text
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join("")
        .toUpperCase();
      return initials || "U";
    },
    canEditOwnContact() {
      return this.resourceHasParametrization(CONTACT_PARAMETRIZATION);
    },
    fontSizeOptions() {
      return [
        {
          value: FONT_SIZE_PREFERENCES.MAIS_ESPACO,
          label: this.$t("Mais_espaco"),
          description: this.$t("Mais_espaco_descricao"),
          badge: this.$t("Tamanho_atual"),
          sampleSize: "16px",
        },
        {
          value: FONT_SIZE_PREFERENCES.PADRAO,
          label: this.$t("Padrao"),
          description: this.$t("Padrao_tamanho_texto_descricao"),
          badge: this.$t("Aumento_de_20"),
          sampleSize: "19.2px",
        },
        {
          value: FONT_SIZE_PREFERENCES.TEXTO_MAIOR,
          label: this.$t("Texto_maior"),
          description: this.$t("Texto_maior_descricao"),
          badge: this.$t("Aumento_de_45"),
          sampleSize: "23.2px",
        },
      ];
    },
    hasFontSizePreferenceChanges() {
      return this.fontSizePreference !== this.savedFontSizePreference;
    },
    hasNotificationsPreferenceChanges() {
      return this.notificationsPreference !== this.savedNotificationsPreference;
    },
    contactEmailChanged() {
      return (
        normalizeEmailValue(this.contactForm.email) !==
        this.profileEmailNormalized
      );
    },
    contactPhoneChanged() {
      return (
        normalizePhoneValue(this.contactForm.telefone) !== this.profilePhoneRaw
      );
    },
    hasContactChanges() {
      return this.contactEmailChanged || this.contactPhoneChanged;
    },
    contactEmailErrors() {
      if (!this.contactTouched.email) {
        return [];
      }
      const message = this.getContactEmailValidationMessage(
        this.contactForm.email,
        this.contactEmailChanged
      );
      return message ? [message] : [];
    },
    contactPhoneErrors() {
      if (!this.contactTouched.telefone) {
        return [];
      }
      const message = this.getContactPhoneValidationMessage(
        this.contactForm.telefone,
        this.contactPhoneChanged
      );
      return message ? [message] : [];
    },
    isContactSubmitDisabled() {
      if (
        !this.canEditOwnContact ||
        this.isContactSubmitting ||
        !this.hasContactChanges
      ) {
        return true;
      }
      return (
        this.getContactEmailValidationMessage(
          this.contactForm.email,
          this.contactEmailChanged
        ) !== null ||
        this.getContactPhoneValidationMessage(
          this.contactForm.telefone,
          this.contactPhoneChanged
        ) !== null
      );
    },
    passwordChangeRequired() {
      return this.$store.getters.isPasswordChangeRequired === true;
    },
    isExpiredPasswordFlow() {
      return (
        this.passwordChangeRequired ||
        String(this.$route?.query?.senhaExpirada || "") === "1"
      );
    },
    passwordChangePolicyDays() {
      return (
        this.$store.state?.inquilino?.prazoMaximoDiasAlteracaoSenha ||
        this.$store.state?.user?.inquilino?.prazoMaximoDiasAlteracaoSenha ||
        null
      );
    },
    passwordChangeMessage() {
      if (this.user?.trocaSenhaPrimeiroAcessoObrigatoria === true) {
        return this.$t("Alteracao_senha_primeiro_acesso_msg");
      }
      if (this.passwordChangePolicyDays) {
        return this.$t("Alteracao_senha_obrigatoria_msg").replace(
          "{dias}",
          this.passwordChangePolicyDays
        );
      }
      return this.$t("Alteracao_senha_obrigatoria_msg_sem_prazo");
    },
    successDialogTitle() {
      if (this.successMessageKey) {
        return this.$t(this.successMessageKey);
      }
      return this.redirectAfterSuccess
        ? this.$t("Senha_atualizada_sucesso")
        : `${this.$t("usuario")} ${this.$t("editado_sucesso")}`;
    },
    senhaHint() {
      return passwordPolicyLabel(this.passwordPolicyType, this.$t.bind(this));
    },
    minimumLengthRequirement() {
      return passwordPolicyLabel(this.passwordPolicyType, this.$t.bind(this));
    },
    passwordHasMinimumLength() {
      return passwordMeetsPolicy(this.form.senha, this.passwordPolicyType);
    },
    passwordsMatch() {
      return (
        Boolean(this.form.senhaConfirma) &&
        this.form.senha === this.form.senhaConfirma
      );
    },
    isPasswordFormValid() {
      return this.passwordHasMinimumLength && this.passwordsMatch;
    },
    senhaErrors() {
      const errors = [];

      if (!this.touched.senha) {
        return errors;
      }

      if (!this.form.senha) {
        errors.push(this.$t("Campo_obrigatorio"));
        return errors;
      }

      if (!this.passwordHasMinimumLength) {
        errors.push(this.minimumLengthRequirement);
      }

      return errors;
    },
    senhaConfirmaErrors() {
      const errors = [];

      if (!this.touched.senhaConfirma) {
        return errors;
      }

      if (!this.form.senhaConfirma) {
        errors.push(this.$t("Campo_obrigatorio"));
        return errors;
      }

      if (this.form.senha !== this.form.senhaConfirma) {
        errors.push(this.$t("senhas_nao_conferem"));
      }

      return errors;
    },
    isSubmitDisabled() {
      return this.isSubmitting || !this.userId || !this.isPasswordFormValid;
    },
  },
  watch: {
    "$store.state.user.pessoa": {
      deep: true,
      handler() {
        if (!this.isEditingContact) {
          this.syncContactForm();
        }
      },
    },
    "$store.state.user.tamanhoFonte": {
      immediate: true,
      handler(value) {
        if (this.isFontSizeSubmitting) {
          return;
        }
        this.syncFontSizePreference(value);
      },
    },
    "$store.state.user.permitirNotificacoes": {
      immediate: true,
      handler(value) {
        if (this.isNotificationsPreferenceSubmitting) {
          return;
        }
        this.syncNotificationsPreference(value);
      },
    },
  },
  mounted() {
    this.refreshUserProfile();
    if (!this.isExpiredPasswordFlow) {
      this.carregarDispositivosConfiaveis();
    }
  },
  beforeDestroy() {
    if (this.hasFontSizePreferenceChanges && !this.isFontSizeSubmitting) {
      applyFontSizePreference(this.savedFontSizePreference);
    }
  },
  methods: {
    refreshUserProfile() {
      this.$store.dispatch("USER_REQUEST").catch((error) => {
        console.warn("USER_REQUEST error on profile load", error);
      });
    },
    syncFontSizePreference(value) {
      const normalized = normalizeFontSizePreference(value);
      this.fontSizePreference = normalized;
      this.savedFontSizePreference = normalized;
      applyFontSizePreference(normalized);
    },
    selectFontSizePreference(value) {
      if (this.isFontSizeSubmitting) {
        return;
      }
      this.fontSizePreference = normalizeFontSizePreference(value);
      applyFontSizePreference(this.fontSizePreference);
      this.clearErrors();
    },
    syncNotificationsPreference(value) {
      const enabled = value !== false;
      this.notificationsPreference = enabled;
      this.savedNotificationsPreference = enabled;
    },
    async saveNotificationsPreference() {
      if (
        this.isNotificationsPreferenceSubmitting ||
        !this.hasNotificationsPreferenceChanges ||
        !this.userId
      ) {
        return;
      }

      this.isNotificationsPreferenceSubmitting = true;
      this.errors = [];
      this.redirectAfterSuccess = false;

      try {
        await UsuarioDataService.updateNotificationsPreference(
          this.notificationsPreference
        );
        this.$store.commit(
          "SET_NOTIFICATIONS_PREFERENCE",
          this.notificationsPreference
        );
        this.savedNotificationsPreference = this.notificationsPreference;
        this.successMessageKey = "Preferencia_notificacoes_atualizada";
        this.submitted = true;
      } catch (error) {
        this.notificationsPreference = this.savedNotificationsPreference;
        this.errors = this.normalizeApiErrors(
          error,
          this.$t("Notificacoes_no_portal")
        );
        this.scrollToTop();
      } finally {
        this.isNotificationsPreferenceSubmitting = false;
      }
    },
    async saveFontSizePreference() {
      if (
        this.isFontSizeSubmitting ||
        !this.hasFontSizePreferenceChanges ||
        !this.userId
      ) {
        return;
      }

      this.isFontSizeSubmitting = true;
      this.errors = [];
      this.redirectAfterSuccess = false;

      try {
        await UsuarioDataService.updateFontSizePreference(
          this.fontSizePreference
        );
        this.$store.commit("SET_FONT_SIZE_PREFERENCE", this.fontSizePreference);
        try {
          await this.$store.dispatch("USER_REQUEST");
        } catch (error) {
          console.warn(
            "USER_REQUEST error after font size preference update",
            error
          );
        }
        this.syncFontSizePreference(this.$store.state.user?.tamanhoFonte);
        this.successMessageKey = "Preferencia_visual_atualizada";
        this.submitted = true;
      } catch (error) {
        console.log(error);
        applyFontSizePreference(this.savedFontSizePreference);
        this.fontSizePreference = this.savedFontSizePreference;
        this.errors = this.normalizeApiErrors(
          error,
          this.$t("Tamanho_do_texto")
        );
        this.scrollToTop();
      } finally {
        this.isFontSizeSubmitting = false;
      }
    },
    carregarDispositivosConfiaveis() {
      this.carregandoDispositivosConfiaveis = true;
      this.dispositivosConfiaveisErro = "";

      UsuarioDataService.getDispositivosConfiaveis()
        .then((response) => {
          this.dispositivosConfiaveis = Array.isArray(response.data)
            ? response.data
            : [];
        })
        .catch((error) => {
          console.log(error);
          this.dispositivosConfiaveis = [];
          this.dispositivosConfiaveisErro = this.$t(
            "Nao_foi_possivel_carregar_dispositivos_confiaveis"
          );
        })
        .finally(() => {
          this.carregandoDispositivosConfiaveis = false;
        });
    },
    removerDispositivoConfiavel(dispositivo) {
      if (!dispositivo?.id || this.removendoDispositivoConfiavelId) {
        return;
      }

      const key = this.trustedDeviceKey(dispositivo);
      this.removendoDispositivoConfiavelId = key;
      const remover = dispositivo.passkey
        ? UsuarioDataService.removerPasskey(dispositivo.id)
        : UsuarioDataService.removerDispositivoConfiavel(dispositivo.id);
      remover
        .then(() => {
          this.dispositivosConfiaveis = this.dispositivosConfiaveis.filter(
            (item) => this.trustedDeviceKey(item) !== key
          );
        })
        .catch((error) => {
          console.log(error);
          this.dispositivosConfiaveisErro = this.$t(
            "Nao_foi_possivel_remover_dispositivo_confiavel"
          );
        })
        .finally(() => {
          this.removendoDispositivoConfiavelId = null;
        });
    },
    trustedDeviceKey(dispositivo) {
      return `${dispositivo?.tipo || "LEGADO"}-${dispositivo?.id || ""}`;
    },
    startContactEdition() {
      if (!this.canEditOwnContact) {
        return;
      }
      this.errors = [];
      this.syncContactForm();
      this.isEditingContact = true;
    },
    cancelContactEdition() {
      this.isEditingContact = false;
      this.isContactSubmitting = false;
      this.syncContactForm();
      this.clearErrors();
    },
    syncContactForm() {
      this.contactForm = {
        email: this.profileEmailNormalized,
        telefone: this.profilePhoneRaw,
      };
      this.contactTouched = {
        email: false,
        telefone: false,
      };
    },
    onContactFieldInput(field) {
      this.contactTouched[field] = true;
      this.clearErrors();
    },
    onPhoneInput(value) {
      this.contactForm.telefone = normalizePhoneValue(value);
      this.contactTouched.telefone = true;
      this.clearErrors();
    },
    async submitContactForm() {
      if (!this.canEditOwnContact || this.isContactSubmitting) {
        return;
      }

      if (!this.hasContactChanges) {
        this.cancelContactEdition();
        return;
      }

      this.contactTouched.email = this.contactEmailChanged;
      this.contactTouched.telefone = this.contactPhoneChanged;
      this.errors = [];

      const validationErrors = this.buildContactValidationErrors();
      if (validationErrors.length > 0) {
        this.errors = validationErrors;
        this.scrollToTop();
        return;
      }

      const nextEmail = normalizeEmailValue(this.contactForm.email);
      const nextPhone = normalizePhoneValue(this.contactForm.telefone);

      this.isContactSubmitting = true;
      this.redirectAfterSuccess = false;

      try {
        if (this.contactEmailChanged) {
          await PessoaDataService.updateMyEmail({ email: nextEmail });
        }

        if (this.contactPhoneChanged) {
          await PessoaDataService.updateMyPhone({ telefone: nextPhone });
        }

        try {
          await this.$store.dispatch("USER_REQUEST");
        } catch (error) {
          console.warn("USER_REQUEST error after contact update", error);
        }

        this.errors = [];
        this.isEditingContact = false;
        this.syncContactForm();
        this.successMessageKey = "Contato_atualizado_sucesso";
        this.submitted = true;
      } catch (error) {
        console.log(error);
        this.errors = this.normalizeApiErrors(error, this.$t("Contato"));
        this.scrollToTop();
      } finally {
        this.isContactSubmitting = false;
      }
    },
    buildContactValidationErrors() {
      const validationErrors = [];
      const emailMessage = this.getContactEmailValidationMessage(
        this.contactForm.email,
        this.contactEmailChanged
      );
      const phoneMessage = this.getContactPhoneValidationMessage(
        this.contactForm.telefone,
        this.contactPhoneChanged
      );

      if (emailMessage) {
        validationErrors.push(this.buildError(this.$t("Email"), emailMessage));
      }
      if (phoneMessage) {
        validationErrors.push(
          this.buildError(this.$t("Telefone"), phoneMessage)
        );
      }

      return validationErrors;
    },
    getContactEmailValidationMessage(value, shouldValidate) {
      if (!shouldValidate) {
        return null;
      }
      const email = normalizeEmailValue(value);
      if (!email) {
        return this.$t("Campo_obrigatorio");
      }
      if (!EMAIL_REGEX.test(email) || email.length < 6) {
        return this.$t("Email_invalido");
      }
      return null;
    },
    getContactPhoneValidationMessage(value, shouldValidate) {
      if (!shouldValidate) {
        return null;
      }
      const phone = normalizePhoneValue(value);
      if (!phone) {
        return this.$t("Campo_obrigatorio");
      }
      if (phone.length < 10 || phone.length > 13) {
        return this.$t("Telefone_invalido");
      }
      return null;
    },
    resourceHasParametrization(nomeParametrizacao) {
      try {
        const parametrizacoes = [
          ...(this.$store.state?.inquilino?.parametrizacoes || []),
          ...(this.user?.inquilino?.parametrizacoes || []),
          ...(this.user?.parametrizacoesPostoTrabalho || []),
        ];
        return parametrizacoes.some(
          (item) => item?.tipo === nomeParametrizacao && item?.ativo === true
        );
      } catch (error) {
        return false;
      }
    },
    formatPhone(value) {
      const raw = normalizePhoneValue(value);
      if (!raw) {
        return "";
      }

      const phone =
        raw.startsWith("55") && (raw.length === 12 || raw.length === 13)
          ? raw.slice(2)
          : raw;

      if (phone.length === 11) {
        return `(${phone.slice(0, 2)}) ${phone.slice(2, 7)}-${phone.slice(7)}`;
      }
      if (phone.length === 10) {
        return `(${phone.slice(0, 2)}) ${phone.slice(2, 6)}-${phone.slice(6)}`;
      }
      return raw;
    },
    formatTrustedDeviceDates(dispositivo) {
      const inclusao = this.formatDateTime(dispositivo?.dtInclusao);
      const ultimoUso = this.formatDateTime(dispositivo?.dtUltimoUso);
      if (ultimoUso) {
        return `${this.$t("Ultimo_uso")}: ${ultimoUso}`;
      }
      if (inclusao) {
        return `${this.$t("Incluido_em")}: ${inclusao}`;
      }
      return "";
    },
    formatDateTime(value) {
      if (!value) {
        return "";
      }
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleString();
    },
    submitForm() {
      this.touched.senha = true;
      this.touched.senhaConfirma = true;
      this.errors = [];

      if (!this.userId) {
        this.errors = [
          this.buildError(
            this.$t("usuario"),
            this.$t("Usuario_nao_encontrado")
          ),
        ];
        this.scrollToTop();
        return;
      }

      if (!this.formIsValid()) {
        this.errors = this.buildValidationErrors();
        this.scrollToTop();
        return;
      }

      this.isSubmitting = true;
      const shouldRedirectAfterSave = this.isExpiredPasswordFlow;

      UsuarioDataService.updateSenha({
        id: this.userId,
        senha: this.form.senha,
      })
        .then(async () => {
          try {
            await this.$store.dispatch("USER_REQUEST");
          } catch (error) {
            console.warn("USER_REQUEST error after password update", error);
          }
          this.errors = [];
          this.redirectAfterSuccess = shouldRedirectAfterSave;
          this.successMessageKey = "Senha_atualizada_sucesso";
          this.submitted = true;
          this.resetForm();
        })
        .catch((error) => {
          console.log(error);
          this.errors = this.normalizeApiErrors(error, this.$t("Nova_senha"));
          this.scrollToTop();
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    formIsValid() {
      return (
        this.senhaErrors.length === 0 && this.senhaConfirmaErrors.length === 0
      );
    },
    buildValidationErrors() {
      const validationErrors = [];

      if (this.senhaErrors.length > 0) {
        validationErrors.push(
          this.buildError(this.$t("Nova_senha"), this.senhaErrors[0])
        );
      }

      if (this.senhaConfirmaErrors.length > 0) {
        validationErrors.push(
          this.buildError(
            this.$t("Confirmar_nova_senha"),
            this.senhaConfirmaErrors[0]
          )
        );
      }

      return validationErrors;
    },
    buildError(fieldName, message) {
      return { fieldName, message };
    },
    normalizeApiErrors(error, fallbackFieldName) {
      const response = error && error.response ? error.response : null;
      const data = response && response.data ? response.data : null;
      const apiErrors = data && data.errors ? data.errors : [];

      if (apiErrors.length > 0) {
        return apiErrors.map((item) =>
          this.buildError(
            this.mapApiFieldName(
              item.fieldName || item.field,
              fallbackFieldName,
              item.message
            ),
            this.mapApiMessage(
              item.message || this.$t("Favor_tente_novamente_mais_tarde")
            )
          )
        );
      }

      const apiMessage = data && data.message ? data.message : null;

      return [
        this.buildError(
          fallbackFieldName || this.$t("Nova_senha"),
          this.mapApiMessage(
            apiMessage || this.$t("Favor_tente_novamente_mais_tarde")
          )
        ),
      ];
    },
    mapApiFieldName(fieldName, fallbackFieldName, message) {
      const apiMessage = `${message || ""}`.trim();
      if (
        apiMessage === "Invalid email" ||
        apiMessage === "Already registered"
      ) {
        return this.$t("Email");
      }
      if (apiMessage === "Invalid phone") {
        return this.$t("Telefone");
      }

      const field = `${fieldName || ""}`.toLowerCase();
      if (field.includes("email")) {
        return this.$t("Email");
      }
      if (field.includes("telefone") || field.includes("phone")) {
        return this.$t("Telefone");
      }
      return fieldName || fallbackFieldName || this.$t("Nova_senha");
    },
    mapApiMessage(message) {
      const normalized = `${message || ""}`.trim();
      if (normalized === "Mandatory field") {
        return this.$t("Campo_obrigatorio");
      }
      if (normalized === "Invalid email") {
        return this.$t("Email_invalido");
      }
      if (normalized === "Invalid phone") {
        return this.$t("Telefone_invalido");
      }
      if (normalized === "Already registered") {
        return this.$t("Email_ja_cadastrado");
      }
      return normalized;
    },
    onPasswordInput() {
      if (!this.isExpiredPasswordFlow) {
        this.touched.senha = true;
      }
      this.clearErrors();
    },
    onPasswordConfirmInput() {
      if (!this.isExpiredPasswordFlow) {
        this.touched.senhaConfirma = true;
      }
      this.clearErrors();
    },
    clearErrors() {
      if (this.errors.length > 0) {
        this.errors = [];
      }
    },
    translateIfPossible(text) {
      if (!text) {
        return "";
      }

      return this.$te(text) ? this.$t(text) : text;
    },
    formatErrorLabel(error) {
      return this.translateIfPossible(
        error && error.fieldName ? error.fieldName : this.$t("Nova_senha")
      );
    },
    formatErrorMessage(error) {
      return this.translateIfPossible(
        error && error.message
          ? error.message
          : this.$t("Favor_tente_novamente_mais_tarde")
      );
    },
    closeSuccessDialog() {
      this.submitted = false;
      const shouldRedirect = this.redirectAfterSuccess;
      if (shouldRedirect) {
        const redirect =
          typeof this.$route.query.redirect === "string"
            ? this.$route.query.redirect
            : "";
        this.redirectAfterSuccess = false;
        this.successMessageKey = "";
        this.$router.replace(
          redirect && redirect !== this.$route.fullPath
            ? redirect
            : "/dashboard"
        );
        return;
      }
      this.successMessageKey = "";
    },
    resetForm() {
      this.form = {
        senha: "",
        senhaConfirma: "",
      };
      this.touched = {
        senha: false,
        senhaConfirma: false,
      };
      this.show1 = false;
      this.show2 = false;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    },
  },
};
</script>

<style scoped>
.profile-page {
  max-width: 1280px;
}

.password-renewal {
  display: grid;
  grid-template-columns: minmax(280px, 0.88fr) minmax(360px, 1.12fr);
  margin: 20px auto 0;
  max-width: 920px;
  overflow: hidden;
}

.password-renewal__intro {
  background: radial-gradient(
      circle at 100% 0,
      rgba(255, 255, 255, 0.14),
      transparent 40%
    ),
    linear-gradient(145deg, #3f5968 0%, #516a78 55%, #657f8d 100%);
  color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 570px;
  padding: 44px 40px;
}

.password-renewal__icon {
  align-items: center;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  height: 62px;
  justify-content: center;
  width: 62px;
}

.password-renewal__eyebrow {
  color: rgba(255, 255, 255, 0.76);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.password-renewal__title {
  color: #ffffff;
  font-size: clamp(1.7rem, 3vw, 2.25rem);
  font-weight: 750;
  letter-spacing: -0.03em;
  line-height: 1.14;
}

.password-renewal__description {
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.96rem;
  line-height: 1.65;
}

.password-renewal__account {
  align-items: center;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 12px;
  display: flex;
  gap: 12px;
  padding: 12px;
}

.password-renewal__avatar {
  background: #ffffff;
  color: #516a78;
  flex: 0 0 auto;
  font-size: 0.9rem;
  font-weight: 800;
}

.password-renewal__account-data {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.password-renewal__account-data strong,
.password-renewal__account-data span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.password-renewal__account-data strong {
  font-size: 0.92rem;
}

.password-renewal__account-data span {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.8rem;
  margin-top: 2px;
}

.password-renewal__security-note {
  align-items: flex-start;
  color: rgba(255, 255, 255, 0.78);
  display: flex;
  font-size: 0.78rem;
  gap: 8px;
  line-height: 1.45;
}

.password-renewal__form-panel {
  align-self: center;
  padding: 44px 48px;
}

.password-renewal__step {
  align-items: center;
  background: #edf2f4;
  border-radius: 999px;
  color: #516a78;
  display: inline-flex;
  font-size: 0.75rem;
  font-weight: 700;
  gap: 6px;
  padding: 6px 11px;
  text-transform: uppercase;
}

.password-renewal__form-title {
  color: #24343d;
  font-size: 1.55rem;
  font-weight: 750;
  letter-spacing: -0.02em;
  line-height: 1.25;
}

.password-requirements {
  background: #f7f9fa;
  border: 1px solid #e5ebee;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 9px;
  padding: 14px 16px;
}

.password-requirements__title {
  color: #53636c;
  font-size: 0.78rem;
  font-weight: 700;
  margin-bottom: 1px;
}

.password-requirement {
  align-items: center;
  color: #7c898f;
  display: flex;
  font-size: 0.82rem;
  gap: 8px;
  transition: color 180ms ease;
}

.password-requirement .v-icon {
  color: #aeb8bd;
  transition: color 180ms ease;
}

.password-requirement--valid,
.password-requirement--valid .v-icon {
  color: #29925b;
}

.password-success-dialog {
  overflow: hidden;
}

.password-success-dialog__icon {
  align-items: center;
  background: linear-gradient(145deg, #3fa66e, #2b8758);
  border-radius: 50%;
  box-shadow: 0 10px 24px rgba(43, 135, 88, 0.24);
  display: flex;
  height: 66px;
  justify-content: center;
  width: 66px;
}

.password-success-dialog__title {
  color: #24343d;
  font-size: 1.35rem;
  line-height: 1.3;
}

.profile-section {
  padding: 24px;
}

.profile-section__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
}

.profile-section__header--center {
  justify-content: center;
  text-align: center;
}

.profile-section__heading {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.profile-section__icon {
  color: #516a78;
  background: #edf2f4;
  border-radius: 8px;
  padding: 8px;
}

.profile-section__eyebrow {
  color: #6f7b85;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.profile-section__title {
  color: #24343d;
  font-size: 1.15rem;
  font-weight: 700;
  line-height: 1.25;
  margin: 2px 0 0;
}

.profile-summary {
  min-height: 100%;
  text-align: center;
}

.profile-summary__avatar {
  display: flex;
  justify-content: center;
  margin-bottom: 18px;
}

.profile-avatar {
  border: 4px solid #ffffff;
  box-shadow: 0 12px 28px rgba(35, 52, 61, 0.16);
}

.profile-avatar--image {
  align-items: center;
  appearance: none;
  background: #edf2f4;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  height: 150px;
  justify-content: center;
  overflow: hidden;
  padding: 0;
  width: 150px;
}

.profile-avatar__img {
  display: block;
  height: 100%;
  object-fit: cover;
  width: 100%;
}

.profile-avatar--placeholder {
  background: #516a78;
  color: #ffffff;
  font-size: 2.15rem;
  font-weight: 700;
}

.profile-summary__name {
  color: #24343d;
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.profile-summary__email {
  color: #6f7b85;
  font-size: 0.9rem;
  margin-top: 6px;
  overflow-wrap: anywhere;
}

.profile-summary__info {
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
}

.profile-info-row,
.contact-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.profile-info-row {
  color: #6f7b85;
}

.profile-info-row strong {
  color: #24343d;
  margin-left: auto;
}

.contact-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.contact-item {
  border: 1px solid #e8edf0;
  border-radius: 8px;
  padding: 14px 16px;
}

.contact-item .v-icon {
  color: #516a78;
}

.contact-item__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.contact-item__content span {
  color: #6f7b85;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
}

.contact-item__content strong {
  color: #24343d;
  font-size: 0.98rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.profile-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}

.font-size-preference__description {
  color: #6f7b85;
  margin: -8px 0 20px;
}

.font-size-options {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.font-size-option {
  align-items: center;
  appearance: none;
  background: #ffffff;
  border: 1px solid #dfe6e9;
  border-radius: 12px;
  color: #24343d;
  cursor: pointer;
  display: flex;
  gap: 14px;
  min-height: 112px;
  padding: 16px;
  text-align: left;
  transition: border-color 160ms ease, box-shadow 160ms ease,
    transform 160ms ease;
  width: 100%;
}

.font-size-option:hover:not(:disabled) {
  border-color: #9babb3;
  box-shadow: 0 8px 20px rgba(50, 73, 84, 0.08);
  transform: translateY(-1px);
}

.font-size-option:focus-visible {
  outline: 3px solid rgba(81, 106, 120, 0.2);
  outline-offset: 2px;
}

.font-size-option--selected {
  background: #f4f7f8;
  border-color: #516a78;
  box-shadow: 0 8px 22px rgba(50, 73, 84, 0.1);
}

.font-size-option:disabled {
  cursor: wait;
  opacity: 0.72;
}

.font-size-option__sample {
  align-items: center;
  background: #e9eef1;
  border-radius: 10px;
  color: #3e5663;
  display: flex;
  flex: 0 0 54px;
  font-weight: 750;
  height: 54px;
  justify-content: center;
  line-height: 1;
}

.font-size-option--selected .font-size-option__sample {
  background: #516a78;
  color: #ffffff;
}

.font-size-option__content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.font-size-option__heading {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.font-size-option__badge {
  background: #e9eef1;
  border-radius: 999px;
  color: #5c6f79;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 8px;
}

.font-size-option__description {
  color: #6f7b85;
  font-size: 0.82rem;
  line-height: 1.45;
  margin-top: 7px;
}

.font-size-option__check {
  flex: 0 0 auto;
}

.font-size-preference__footer {
  align-items: center;
  border-top: 1px solid #edf1f3;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-top: 20px;
  padding-top: 18px;
}

.font-size-preference__hint {
  align-items: center;
  color: #6f7b85;
  display: inline-flex;
  font-size: 0.82rem;
  gap: 7px;
}

.notification-preference {
  align-items: center;
  background: #f7f9fa;
  border: 1px solid #e4eaed;
  border-radius: 12px;
  display: flex;
  gap: 20px;
  justify-content: space-between;
  padding: 18px 20px;
}

.notification-preference__copy {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.notification-preference__copy strong {
  color: #344b57;
}

.notification-preference__copy span {
  color: #6f7b85;
  font-size: 0.86rem;
  line-height: 1.45;
}

.trusted-devices-list {
  border: 1px solid #e8edf0;
  border-radius: 8px;
}

@media (max-width: 600px) {
  .password-renewal {
    margin-top: 0;
  }

  .password-renewal__intro,
  .password-renewal__form-panel {
    padding: 30px 22px;
  }

  .password-renewal__intro {
    min-height: auto;
  }

  .profile-section {
    padding: 18px;
  }

  .profile-section__header {
    align-items: flex-start;
  }

  .profile-actions {
    align-items: stretch;
    flex-direction: column-reverse;
  }

  .profile-actions .v-btn {
    width: 100%;
  }

  .font-size-preference__footer {
    align-items: stretch;
    flex-direction: column;
  }

  .font-size-preference__footer .v-btn {
    width: 100%;
  }

  .notification-preference {
    align-items: flex-start;
    padding: 16px;
  }
}

@media (max-width: 900px) {
  .password-renewal {
    grid-template-columns: 1fr;
  }

  .password-renewal__intro {
    min-height: auto;
  }

  .font-size-options {
    grid-template-columns: 1fr;
  }
}
</style>
